import { useState, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '../api/client'
import { cn } from '../utils'
import sharedStyles from './PageStyles.module.css'
import styles from './AudioEvalPage.module.css'

const PAGE_SIZE = 20

interface EvalItem {
  release_id: number
  video_id: string
  video_title: string
  match_type: string
  release_title: string
  artist: string
  year: number | null
  predicted_genres: string | null
  predicted_styles: string | null
  confidence: number | null
  tag_agreement: number | null
  danceability: number | null
  approachability: number | null
  engagement: number | null
  mood_party: number | null
  mood_aggressive: number | null
  mood_relaxed: number | null
  voice_instrumental: number | null
  instruments_top5: string | null
  mood_theme_top5: string | null
  bpm: number | null
  bpm_confidence: number | null
  key: string | null
  scale: string | null
  key_strength: number | null
  spectral_centroid: number | null
}

function pct(v: number | null) {
  if (v == null) return '—'
  return `${Math.round(v * 100)}%`
}

function num(v: number | null, decimals = 1) {
  if (v == null) return '—'
  return v.toFixed(decimals)
}

function parseArtist(json: string | null): string {
  if (!json) return ''
  try {
    const arr = JSON.parse(json)
    return Array.isArray(arr) ? arr.map((a: any) => a.name).join(', ') : String(arr)
  } catch { return json }
}

function parseTop5(json: string | null): { label: string; score: number }[] {
  if (!json) return []
  try { return JSON.parse(json) } catch { return [] }
}

function parseDict(json: string | null): Record<string, number> {
  if (!json) return {}
  try { return JSON.parse(json) } catch { return {} }
}

function ScoreBar({ label, value }: { label: string; value: number | null }) {
  const pctVal = value != null ? Math.round(value * 100) : null
  return (
    <div className={styles.scoreBar}>
      <div className={styles.scoreLabelRow}>
        <span className={styles.scoreLabel}>{label}</span>
        <span className={styles.scoreValue}>{pctVal != null ? `${pctVal}%` : '—'}</span>
      </div>
      <div className={sharedStyles.progressBarTrack} style={{ height: 4 }}>
        {pctVal != null && (
          <div className={sharedStyles.progressBarFill} style={{ width: `${pctVal}%` }} />
        )}
      </div>
    </div>
  )
}

function EvalCard({ item }: { item: EvalItem }) {
  const genres   = parseDict(item.predicted_genres)
  const styleDict = parseDict(item.predicted_styles)
  const inst     = parseTop5(item.instruments_top5).filter(e => e.score >= 0.05)
  const themes   = parseTop5(item.mood_theme_top5).filter(e => e.score >= 0.05)

  const topGenres = Object.entries(genres).slice(0, 5)
  const topStyles = Object.entries(styleDict).slice(0, 5)

  return (
    <div className={styles.card}>
      {/* YouTube embed */}
      <div className={styles.videoSection}>
        <iframe
          className={styles.iframe}
          src={`https://www.youtube.com/embed/${item.video_id}`}
          title={item.video_title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
        <div className={styles.videoMeta}>
          <div className={styles.releaseInfo}>
            {parseArtist(item.artist)} — {item.release_title} {item.year ? `(${item.year})` : ''}
          </div>
          <div className={styles.videoTitle}>{item.video_title}</div>
          <div>
            <span className={styles.matchBadge} style={{
              background: item.match_type === 'album' ? 'rgba(74, 150, 74, 0.2)' : item.match_type === 'track' ? 'rgba(74, 110, 150, 0.2)' : 'rgba(150, 74, 74, 0.2)',
              color: item.match_type === 'album' ? '#86efac' : item.match_type === 'track' ? '#93c5fd' : '#fca5a5',
            }}>{item.match_type}</span>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className={styles.featuresGrid}>
        {/* DSP */}
        <div>
          <div className={styles.sectionTitle}>DSP</div>
          <div style={{ fontSize: 12, lineHeight: 1.8 }}>
            <div><span style={{ color: '#888' }}>BPM </span><span style={{ color: '#eee' }}>{num(item.bpm, 1)}</span>
              {item.bpm_confidence != null && <span style={{ color: '#555', fontSize: 10 }}> ({pct(item.bpm_confidence)})</span>}
            </div>
            <div><span style={{ color: '#888' }}>Key </span><span style={{ color: '#eee' }}>{item.key ?? '—'} {item.scale ?? ''}</span>
              {item.key_strength != null && <span style={{ color: '#555', fontSize: 10 }}> ({pct(item.key_strength)})</span>}
            </div>
            <div><span style={{ color: '#888' }}>Centroid </span><span style={{ color: '#eee' }}>{num(item.spectral_centroid, 0)} Hz</span></div>
          </div>
        </div>

        {/* Classifiers */}
        <div>
          <div className={styles.sectionTitle}>Classifiers</div>
          <ScoreBar label="Danceability"     value={item.danceability} />
          <ScoreBar label="Approachability"  value={item.approachability} />
          <ScoreBar label="Engagement"       value={item.engagement} />
          <ScoreBar label="Party"            value={item.mood_party} />
          <ScoreBar label="Aggressive"       value={item.mood_aggressive} />
          <ScoreBar label="Relaxed"          value={item.mood_relaxed} />
          <ScoreBar label="Vocal"            value={item.voice_instrumental} />
        </div>

        {/* Tags */}
        <div>
          <div className={styles.sectionTitle}>Tags</div>
          {topGenres.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div className={sharedStyles.filterGroup}><label>Genres</label></div>
              {topGenres.map(([label, score]) => (
                <div key={label} className={styles.tagRow}>
                  <span style={{ color: '#ccc' }}>{label}</span>
                  <span className={styles.tagScore}>{(score as number).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
          {topStyles.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div className={sharedStyles.filterGroup}><label>Styles</label></div>
              {topStyles.map(([label, score]) => (
                <div key={label} className={styles.tagRow}>
                  <span style={{ color: '#ccc' }}>{label}</span>
                  <span className={styles.tagScore}>{(score as number).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
          {inst.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div className={sharedStyles.filterGroup}><label>Instruments</label></div>
              {inst.map(e => (
                <div key={e.label} className={styles.tagRow}>
                  <span style={{ color: '#ccc' }}>{e.label}</span>
                  <span className={styles.tagScore}>{e.score.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
          {themes.length > 0 && (
            <div>
              <div className={sharedStyles.filterGroup}><label>Mood/Theme</label></div>
              {themes.map(e => (
                <div key={e.label} className={styles.tagRow}>
                  <span style={{ color: '#ccc' }}>{e.label}</span>
                  <span className={styles.tagScore}>{e.score.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
          {(item.confidence != null || item.tag_agreement != null) && (
            <div style={{ marginTop: 12, fontSize: 10, color: '#555', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: 8 }}>
              confidence {pct(item.confidence)} · agreement {pct(item.tag_agreement)}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function Pager({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (p: number) => void }) {
  if (totalPages <= 1) return null

  // Show at most 7 page buttons: first, last, current ±2, and ellipses
  const pages: (number | '…')[] = []
  const add = (n: number) => { if (!pages.includes(n)) pages.push(n) }
  add(1)
  for (let i = Math.max(2, page - 2); i <= Math.min(totalPages - 1, page + 2); i++) add(i)
  add(totalPages)

  const withEllipsis: (number | '…')[] = []
  let prev = 0
  for (const p of pages as number[]) {
    if (p - prev > 1) withEllipsis.push('…')
    withEllipsis.push(p)
    prev = p
  }

  return (
    <div className={sharedStyles.pagination}>
      <button
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        className={sharedStyles.pageButton}
      >‹</button>
      {withEllipsis.map((p, i) =>
        p === '…'
          ? <span key={`e${i}`} style={{ color: '#555', padding: '0 4px' }}>…</span>
          : <button key={p} onClick={() => onChange(p as number)} className={cn(sharedStyles.pageButton, p === page && sharedStyles.toolbarButtonActive)}>{p}</button>
      )}
      <button
        onClick={() => onChange(page + 1)}
        disabled={page === totalPages}
        className={sharedStyles.pageButton}
      >›</button>
    </div>
  )
}

export function AudioEvalPage() {
  const [page, setPage] = useState(1)
  const scrollRef = useRef<HTMLDivElement>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['audio-eval', page],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items: EvalItem[]; total: number; page: number; limit: number }>(
        '/audio-eval', { params: { page, limit: PAGE_SIZE } }
      )
      return data
    },
  })

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 1

  const goTo = (p: number) => {
    setPage(p)
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Scroll to top when data loads for a new page
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 })
  }, [data])

  return (
    <div className={sharedStyles.page}>
      <div className={sharedStyles.pageHeader}>
        <h1>Audio Eval</h1>
        {data && (
          <span className={sharedStyles.countBadge}>{data.total.toLocaleString()} videos · page {page} / {totalPages}</span>
        )}
      </div>

      <div ref={scrollRef} className={sharedStyles.gridView}>
        <div style={{ margin: '16px 0' }}>
          <Pager page={page} totalPages={totalPages} onChange={goTo} />
        </div>

        {isLoading && <div className={sharedStyles.empty}><i className="bi bi-arrow-repeat spinning" /> Loading evaluation data...</div>}
        {data?.items.map(item => <EvalCard key={`${item.release_id}_${item.video_id}`} item={item} />)}

        <Pager page={page} totalPages={totalPages} onChange={goTo} />
      </div>
    </div>
  )
}
